import {cloneDeep} from 'lodash';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {StorageService} from '../../services/storage/storage.service';
import {GuidService} from '../../services/guid/guid.service';
import {environment} from '../../../environments/environment';
import {IMedicalInheritedFamilyDisease} from './medical-inherited-family-disease.interface';
import {IRecordMedicalInheritedFamily} from './record-medical-inherited-family.interface';
import {firstValueFrom} from 'rxjs';

export interface IRRecordMedicalInheritedFamily {
  medicalInheritedFamily: IRecordMedicalInheritedFamily;
  wasDeployed?: boolean;
}

export namespace RecordMedicalInheritedFamilyAction {
  export class GetActive {
    static readonly type = '[Record] Get Record Medical InheritedFamily Action';
    constructor(public serviceId: string, public patientId: string) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch Record Medical InheritedFamily Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public data: Partial<IRecordMedicalInheritedFamily>
    ) { }
  }

  export class PostDisease {
    static readonly type = '[Record] Post Medical InheritedFamily Disease Action';
    constructor(public serviceId: string, public patientId: string, public data: Partial<IMedicalInheritedFamilyDisease>) { }
  }

  export class DeleteDiseaseById {
    static readonly type = '[Record] Delete Medical InheritedFamily Disease Action';
    constructor(public serviceId: string, public patientId: string, public diseaseId: string) { }
  }

  export class Reset {
    static readonly type = '[Record] Reset Record Medical Personal Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Record Medical Inherited Family Action';
    constructor(public value: boolean) { }
  }
}

@State<IRRecordMedicalInheritedFamily>({
  name: 'MedicalInheritedFamilyDiseaseState',
  defaults: {
    medicalInheritedFamily: null
  },
})

@Injectable()
export class MedicalInheritedFamilyDiseaseState {
  constructor(
    private baseService: BaseService<any>,
    private storageService: StorageService,
    private guidService: GuidService,
    private store: Store
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getRecordMedicalInheritedFamily(state: IRRecordMedicalInheritedFamily): IRecordMedicalInheritedFamily {
    return state.medicalInheritedFamily;
  }

  @Selector()
  public static getWasDeployed(state: IRRecordMedicalInheritedFamily): boolean {
    return state.wasDeployed;
  }

  @Action(RecordMedicalInheritedFamilyAction.GetActive)
  async getRecordMedicalInheritedFamilyAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    action: RecordMedicalInheritedFamilyAction.GetActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordMedicalInheritedFamily> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-inherited-family/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      medicalInheritedFamily: response.entity
    });
  }

  @Action(RecordMedicalInheritedFamilyAction.PatchActive)
  async patchRecordMedicalInheritedFamilyAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    action: RecordMedicalInheritedFamilyAction.PatchActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-inherited-family`, action.data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordMedicalInheritedFamilyAction.PostDisease)
  async postMedicalInheritedFamilyDiseaseAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    action: RecordMedicalInheritedFamilyAction.PostDisease
  ): Promise<void> {
    const response: DataBaseServiceResponse<IMedicalInheritedFamilyDisease> = await firstValueFrom(
      (this.baseService.post(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/medical-inherited-family/disease`, action.data))
    );

    if (response.error) throw new Error(response.message);

    let record: IRecordMedicalInheritedFamily = cloneDeep(ctx.getState().medicalInheritedFamily);
    if (record.diseases) {
      record.diseases.push(response.entity);
    } else {
      record = {
        diseases: [response.entity],
        expertId: null,
        patientId: null,
        serviceId: null
      };
    }

    ctx.patchState({
      medicalInheritedFamily: record
    });
  }

  @Action(RecordMedicalInheritedFamilyAction.DeleteDiseaseById)
  async deleteMedicalInheritedFamilyDiseaseAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    {diseaseId, serviceId, patientId}: RecordMedicalInheritedFamilyAction.DeleteDiseaseById
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/patients/${patientId}/service/${serviceId}/records/medical-inherited-family/disease/${diseaseId}`)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordMedicalInheritedFamilyAction.Reset)
  async resetRecordMedicalInheritedFamilyAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    _: RecordMedicalInheritedFamilyAction.Reset
  ): Promise<void> {
    ctx.patchState({
      medicalInheritedFamily: null
    });
  }

  @Action(RecordMedicalInheritedFamilyAction.Deploy)
  async WasDeployedRecordMedicalInheritedFamilyAction(
    ctx: StateContext<IRRecordMedicalInheritedFamily>,
    action: RecordMedicalInheritedFamilyAction.Deploy
  ): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
